class CartRecommendations extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
  }

  connectedCallback() {
    this.performRecommendations();
    this.injectVariantStyles();
  }

  performRecommendations() {
    const recommendations = this.querySelector("[data-recommendations]");
    if (!recommendations) return;

    // If we have a data-url, fetch dynamic recommendations
    if (this.dataset.url) {
      this.fetchRecommendations(recommendations);
    } else {
      // Initialize with static products
      this.initSlider();
    }
  }

  async fetchRecommendations(container) {
    try {
      const url = new URL(this.dataset.url, window.location.origin);
      const productId = url.searchParams.get("product_id");
      const limit = url.searchParams.get("limit") || 6;
      const intent = url.searchParams.get("intent") || "complementary";

      // Create the direct recommendations URL
      const recommendationsUrl = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

      // console.log("Fetching from:", recommendationsUrl);

      const response = await fetch(recommendationsUrl);
      const data = await response.json();

      // console.log("Recommendations data:", data);

      if (data.products && data.products.length > 0) {
        container.innerHTML = "";

        // Store product data for variant matching
        this.productsData = data.products;

        data.products.forEach((product) => {
          const productData = {
            id: product.id,
            url: product.url,
            title: product.title,
            price: this.formatMoney(product.price_min),
            compareAtPrice:
              product.compare_at_price_min > product.price_min
                ? this.formatMoney(product.compare_at_price_min)
                : null,
            image: product.featured_image ? product.featured_image : null,
            variantId: product.variants[0]?.id || null,
            available: product.available || false,
            variants: product.variants || [],
            options: product.options || [],
          };
          const productHTML = this.createProductHTML(productData);
          container.insertAdjacentHTML("beforeend", productHTML);
        });

        this.initSlider();
        this.initAddToCartHandlers();
      } else {
        this.classList.add("hidden");
      }
    } catch (error) {
      console.error("Error fetching recommendations:", error);
      this.initSlider();
      this.classList.add("hidden");
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR", 
    }).format(cents / 100);
  }

  createProductHTML(product) {
    // Find the first available variant or use the first variant
    const firstAvailableVariant = this.findFirstAvailableVariant(product);
    const selectedVariant = firstAvailableVariant || product.variants[0];
    
    // Use the selected variant's price instead of product-level price
    const variantPrice = selectedVariant ? this.formatMoney(selectedVariant.price) : product.price;
    const variantComparePrice = selectedVariant && selectedVariant.compare_at_price && selectedVariant.compare_at_price > selectedVariant.price 
      ? this.formatMoney(selectedVariant.compare_at_price) 
      : null;
    
    const priceHTML = variantComparePrice
      ? `<del>${variantComparePrice}</del><ins>${variantPrice}</ins>`
      : variantPrice;

    // Generate variant selectors if product has options
    const variantSelectors = this.createVariantSelectors(product);

    return `
      <div class="cart-recommendations__item swiper-slide">
        <a href="${product.url}" class="cart-recommendations__item-link">
          ${
            product.image
              ? `
            <div class="cart-recommendations__item-media">
              <div class="media">
                <img src="${product.image}" width="400" height="auto" loading="lazy">
              </div>
            </div>
          `
              : ""
          }
          
        </a>
        <div class="upsell_add-to-cart">
          <div class="cart-recommendations__item-details">
            <h6 class="cart-recommendations__item-title h6">${product.title}</h6>
            <p class="cart-recommendations__item-price">${priceHTML}</p>
          </div>
          <product-form class="card__add-to-cart card__button">
            <form action="/cart/add" method="post" enctype="multipart/form-data" class="form" data-type="add-to-cart-form">
              <input type="hidden" name="form_type" value="product">
              <input type="hidden" name="utf8" value="✓">
              <input type="hidden" name="id" value="${selectedVariant?.id || product.variantId}">
              
              
              ${variantSelectors}
              <button type="submit" class="button button--primary" ${
                !selectedVariant?.available ? "disabled" : ""
              }>
                <span class="card__quickview-text">
                <svg class="icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 9.25V13.25" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"></path>
                <path d="M12 11.25L8 11.25" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"></path>
                <path d="M16.3125 5.625H3.6875C3.53371 5.62572 3.38545 5.68242 3.27042 5.78451C3.1554 5.88659 3.08149 6.02707 3.0625 6.17969L1.95313 16.1797C1.94326 16.2668 1.95184 16.355 1.9783 16.4386C2.00477 16.5222 2.04853 16.5993 2.10674 16.6649C2.16495 16.7305 2.2363 16.7831 2.31617 16.8193C2.39604 16.8555 2.48263 16.8745 2.57031 16.875H17.4297C17.5174 16.8745 17.604 16.8555 17.6838 16.8193C17.7637 16.7831 17.8351 16.7305 17.8933 16.6649C17.9515 16.5993 17.9952 16.5222 18.0217 16.4386C18.0482 16.355 18.0567 16.2668 18.0469 16.1797L16.9375 6.17969C16.9185 6.02707 16.8446 5.88659 16.7296 5.78451C16.6146 5.68242 16.4663 5.62572 16.3125 5.625Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M6.25 5C6.25 4.00544 6.64509 3.05161 7.34835 2.34835C8.05161 1.64509 9.00544 1.25 10 1.25C10.9946 1.25 11.9484 1.64509 12.6517 2.34835C13.3549 3.05161 13.75 4.00544 13.75 5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                </span>
                <div class="loading-overlay__spinner hidden"><svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                  <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                </svg></div>
              </button>
              <input type="hidden" name="product-id" value="${product.id}">
            </form>
          </product-form>
        </div>
      </div>
    `;
  }

  findFirstAvailableVariant(product) {
    if (!product.variants || product.variants.length === 0) return null;
    
    return product.variants.find(variant => variant.available) || null;
  }

  initSlider() {
    if (this.slider) {
      this.slider.destroy(true, true);
    }

    this.slider = new Swiper(this.querySelector(".swiper"), {
      slidesPerView: "auto",
      navigation: {
        nextEl: ".cart-rec-navs__next",
        prevEl: ".cart-rec-navs__prev",
      }
    });
  }

  initAddToCartHandlers() {
    const forms = this.querySelectorAll('[data-type="add-to-cart-form"]');
    
    console.log('=== initAddToCartHandlers Debug ===');
    console.log('Found forms:', forms.length);
    
    forms.forEach((form, formIndex) => {
      console.log(`Form ${formIndex}:`, form);
      
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.handleAddToCart(event.target);
      });
      
      // Handle variant selector changes
      const variantSelectors = form.querySelectorAll('select[data-option-index]');
      console.log(`Form ${formIndex} - Found variant selectors:`, variantSelectors.length);
      
      variantSelectors.forEach((selector, selectorIndex) => {
        console.log(`Form ${formIndex}, Selector ${selectorIndex}:`, selector);
        console.log(`Selector ID: ${selector.id}, Name: ${selector.name}, Data attributes:`, selector.dataset);
        
        selector.addEventListener('change', (event) => {
          console.log('=== Select Change Event Triggered ===');
          console.log('Event target:', event.target);
          console.log('Event target value:', event.target.value);
          this.handleVariantChange(event.target, form);
        });
        
        // Also add a click event to test if the selector is interactive
        selector.addEventListener('click', (event) => {
          console.log('=== Select Click Event Triggered ===');
          console.log('Click event on selector:', event.target);
        });
      });
    });
  }

  async handleAddToCart(form) {
    const button = form.querySelector('button[type="submit"]');
    const spinner = button.querySelector('.loading-overlay__spinner');
    const buttonText = button.querySelector('.card__quickview-text');
    
    // Show loading state
    button.disabled = true;
    spinner.classList.remove('hidden');
    buttonText.textContent = 'Adding...';
    
    try {
      const formData = new FormData(form);
      
      // Ensure we have the correct variant ID by explicitly getting it from the form
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        // Remove any existing 'id' entry and add the current one
        formData.delete('id');
        formData.append('id', variantInput.value);
        
        // Debug log to verify the variant ID being used
        console.log('Adding to cart - Variant ID:', variantInput.value);
      }
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Show success message
        buttonText.textContent = 'Added!';
        setTimeout(() => {
          buttonText.textContent = 'Add to Cart';
        }, 2000);
        
        // Trigger cart update using the theme's system
        this.triggerCartUpdate();
        
        // Open cart drawer after a short delay to ensure cart is updated
        setTimeout(() => {
          this.openCartDrawer();
        }, 300);
        
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      buttonText.textContent = 'Error';
      setTimeout(() => {
        buttonText.textContent = 'Add to Cart';
      }, 2000);
    } finally {
      // Hide loading state
      button.disabled = false;
      spinner.classList.add('hidden');
    }
  }

  openCartDrawer() {
    // Find and open the cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    }
  }

  reinitializeCartComponents() {
    // This method is now simplified since triggerCartUpdate handles the logic
    // Keep it for backward compatibility but it's no longer needed
  }

  initAccordion() {
    $(".reco__accordion-toggle").click(function () {
      if (!$(this).hasClass("active")) {
        $(this).addClass("active");
        $(this)
          .siblings(".about__accordion-description")
          .eq($(this).index())
          .stop()
          .slideDown(300);
      } else {
        $(this).removeClass("active");
        $(this).siblings(".about__accordion-description").stop().slideUp(300);
      }
    });
  }

  createVariantSelectors(product) {
    if (!product.options || product.options.length === 0) {
      return '';
    }

    // Find the first available variant for initial selection
    const firstAvailableVariant = this.findFirstAvailableVariant(product);
    const selectedVariant = firstAvailableVariant || product.variants[0];

    let selectorsHTML = '';
    
    product.options.forEach((option, optionIndex) => {
      if (option.values && option.values.length > 0) {
        // Get available values with availability status for this option
        const availableValuesWithStatus = this.getAvailableOptionValuesWithAvailability(product, optionIndex);
        
        if (availableValuesWithStatus.length > 0) {
          selectorsHTML += `
            <div class="product-form__input">
              <label class="form__label" for="Option${optionIndex}-${product.id}">
                ${option.name}
              </label>
              <div class="select">
                <select
                  id="Option${optionIndex}-${product.id}"
                  class="select__select"
                  name="options[${option.name}]"
                  data-option-index="${optionIndex}"
                  data-product-id="${product.id}"
                >
                  ${availableValuesWithStatus.map(({ value, available }) => {
                    let isSelected = false;
                    if (selectedVariant) {
                      if (optionIndex === 0) isSelected = selectedVariant.option1 === value;
                      else if (optionIndex === 1) isSelected = selectedVariant.option2 === value;
                      else if (optionIndex === 2) isSelected = selectedVariant.option3 === value;
                    }
                    return `
                      <option value="${value}" ${isSelected ? 'selected' : ''} ${!available ? 'disabled' : ''}>
                        ${value}${!available ? '' : ''}
                      </option>
                    `;
                  }).join('')}
                </select>
              </div>
            </div>
          `;
        }
      }
    });

    return selectorsHTML;
  }

  getAvailableOptionValues(product, optionIndex) {
    if (!product.variants) return [];
    
    // Get all available variants (variants that are in stock)
    const availableVariants = product.variants.filter(variant => variant.available);
    
    if (availableVariants.length === 0) {
      // If no variants are available, return all values but mark them as unavailable
      return product.options[optionIndex].values;
    }
    
    // Get unique values for this option from available variants
    const availableValues = new Set();
    availableVariants.forEach(variant => {
      let value;
      if (optionIndex === 0) value = variant.option1;
      else if (optionIndex === 1) value = variant.option2;
      else if (optionIndex === 2) value = variant.option3;
      
      if (value) {
        availableValues.add(value);
      }
    });
    
    return Array.from(availableValues);
  }

  getAvailableOptionValuesWithAvailability(product, optionIndex) {
    if (!product.variants) return [];
    
    // Get all variants and their availability status
    const allVariants = product.variants;
    
    // Create a map to track availability for each option value
    const valueAvailability = new Map();
    
    allVariants.forEach(variant => {
      let value;
      if (optionIndex === 0) value = variant.option1;
      else if (optionIndex === 1) value = variant.option2;
      else if (optionIndex === 2) value = variant.option3;
      
      if (value) {
        // If we haven't seen this value yet, or if this variant is available, update the availability
        if (!valueAvailability.has(value) || variant.available) {
          valueAvailability.set(value, variant.available);
        }
      }
    });
    
    return Array.from(valueAvailability.entries()).map(([value, available]) => ({
      value,
      available
    }));
  }

  getAvailableOptionValuesForCurrentSelection(product, optionIndex, currentSelections) {
    if (!product.variants) return [];
    
    // Get all available variants
    const availableVariants = product.variants.filter(variant => variant.available);
    
    if (availableVariants.length === 0) {
      return product.options[optionIndex].values;
    }
    
    // Filter variants based on current selections (excluding the current option)
    const filteredVariants = availableVariants.filter(variant => {
      return Object.keys(currentSelections).every(optionName => {
        const optionValue = currentSelections[optionName];
        const optionIndexToCheck = product.options.findIndex(opt => opt.name === optionName);
        
        // Skip the option we're currently checking
        if (optionIndexToCheck === optionIndex) return true;
        
        // Check if the variant matches the selected option
        if (optionIndexToCheck === 0) return variant.option1 === optionValue;
        if (optionIndexToCheck === 1) return variant.option2 === optionValue;
        if (optionIndexToCheck === 2) return variant.option3 === optionValue;
        
        return false;
      });
    });
    
    // Get unique values for this option from filtered variants
    const availableValues = new Set();
    filteredVariants.forEach(variant => {
      let value;
      if (optionIndex === 0) value = variant.option1;
      else if (optionIndex === 1) value = variant.option2;
      else if (optionIndex === 2) value = variant.option3;
      
      if (value) {
        availableValues.add(value);
      }
    });
    
    return Array.from(availableValues);
  }

  handleVariantChange(selector, form) {
    const productId = selector.dataset.productId;
    const selectedOptions = this.getSelectedOptions(form);
    
    console.log('=== Variant Change Debug ===');
    console.log('Product ID:', productId);
    console.log('Selected Options:', selectedOptions);
    console.log('All Products Data:', this.productsData);
    
    // Find the matching variant
    const matchingVariant = this.findMatchingVariant(productId, selectedOptions);
    
    console.log('Matching Variant:', matchingVariant);
    
    if (matchingVariant) {
      // Update the variant ID in the form
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        console.log('Before update - Variant Input Value:', variantInput.value);
        variantInput.value = matchingVariant.id;
        console.log('After update - Variant Input Value:', variantInput.value);
        console.log('Variant changed - New Variant ID:', matchingVariant.id, 'Options:', selectedOptions);
      }
      
      // Update price if available
      this.updateProductPrice(form, matchingVariant);
      
      // Update button state based on availability
      this.updateButtonState(form, matchingVariant);
    } else {
      console.log('No matching variant found!');
    }
    
    // Update available options for other selectors
    this.updateAvailableOptions(form, productId, selectedOptions);
  }

  updateAvailableOptions(form, productId, currentSelections) {
    if (!this.productsData) return;
    
    const product = this.productsData.find(p => p.id.toString() === productId.toString());
    if (!product || !product.options) return;
    
    // Update each selector with available options
    product.options.forEach((option, optionIndex) => {
      const selector = form.querySelector(`select[data-option-index="${optionIndex}"]`);
      if (!selector) return;
      
      // Get available values for this option based on current selections
      const availableValues = this.getAvailableOptionValuesForCurrentSelection(product, optionIndex, currentSelections);
      
      // Get all values with availability status for this option
      const allValuesWithStatus = this.getAvailableOptionValuesWithAvailability(product, optionIndex);
      
      // Store current selection
      const currentValue = selector.value;
      
      // Create a map of available values for quick lookup
      const availableValuesSet = new Set(availableValues);
      
      // Update options with availability status
      selector.innerHTML = allValuesWithStatus.map(({ value, available }) => {
        const isCurrentlyAvailable = availableValuesSet.has(value);
        const isSelected = value === currentValue;
        
        return `
          <option value="${value}" ${isSelected ? 'selected' : ''} ${!isCurrentlyAvailable ? 'disabled' : ''}>
            ${value}${!isCurrentlyAvailable ? ' (Sold Out)' : ''}
          </option>
        `;
      }).join('');
      
      // If current selection is no longer available and there are other options, select the first available option
      if (!availableValues.includes(currentValue) && availableValues.length > 0) {
        selector.value = availableValues[0];
        // Trigger change event to update variant
        selector.dispatchEvent(new Event('change'));
      }
    });
  }

  updateButtonState(form, variant) {
    const button = form.querySelector('button[type="submit"]');
    if (!button) return;
    
    if (variant.available) {
      button.disabled = false;
      button.classList.remove('disabled');
    } else {
      button.disabled = true;
      button.classList.add('disabled');
    }
  }

  getSelectedOptions(form) {
    const options = {};
    const selectors = form.querySelectorAll('select[data-option-index]');
    
    console.log('=== getSelectedOptions Debug ===');
    console.log('Found selectors:', selectors.length);
    
    selectors.forEach(selector => {
      const optionName = selector.name.replace('options[', '').replace(']', '');
      options[optionName] = selector.value;
      console.log(`Selector: ${optionName} = "${selector.value}"`);
    });
    
    console.log('Final options object:', options);
    return options;
  }

  findMatchingVariant(productId, selectedOptions) {
    if (!this.productsData) return null;
    
    const product = this.productsData.find(p => p.id.toString() === productId.toString());
    if (!product || !product.variants) return null;
    
    console.log('=== findMatchingVariant Debug ===');
    console.log('Looking for product:', productId);
    console.log('Found product:', product);
    console.log('Product variants:', product.variants);
    console.log('Selected options:', selectedOptions);
    
    // Find the variant that matches all selected options
    const matchingVariant = product.variants.find(variant => {
      // Check if all selected options match the variant
      const matches = Object.keys(selectedOptions).every(optionName => {
        const optionValue = selectedOptions[optionName];
        const optionIndex = product.options.findIndex(opt => opt.name === optionName);
        
        let variantValue;
        if (optionIndex === 0) variantValue = variant.option1;
        else if (optionIndex === 1) variantValue = variant.option2;
        else if (optionIndex === 2) variantValue = variant.option3;
        
        const isMatch = variantValue === optionValue;
        console.log(`Option ${optionName}: selected="${optionValue}", variant="${variantValue}", match=${isMatch}`);
        
        return isMatch;
      });
      
      console.log(`Variant ${variant.id}: matches=${matches}`);
      return matches;
    });
    
    console.log('Final matching variant:', matchingVariant);
    return matchingVariant || null;
  }

  updateProductPrice(form, variant) {
    const priceElement = form.closest('.cart-recommendations__item').querySelector('.cart-recommendations__item-price');
    if (priceElement && variant.price) {
      const variantPrice = this.formatMoney(variant.price);
      const variantComparePrice = variant.compare_at_price && variant.compare_at_price > variant.price 
        ? this.formatMoney(variant.compare_at_price) 
        : null;
      
      const priceHTML = variantComparePrice
        ? `<del>${variantComparePrice}</del><ins>${variantPrice}</ins>`
        : variantPrice;
      
      priceElement.innerHTML = priceHTML;
    }
  }

  injectVariantStyles() {
    // Add CSS for variant selectors if not already added
    if (!document.getElementById('cart-recommendations-variant-styles')) {
      const style = document.createElement('style');
      style.id = 'cart-recommendations-variant-styles';
      style.textContent = `
        .cart-recommendations__item .form__label {
          font-size: 12px;
          font-weight: 500;
          margin-bottom: 4px;
          display: block;
        }
        .cart-recommendations__item .select {
          position: relative;
        }
        .cart-recommendations__item .select__select {
          width: 100%;
          padding: 6px 8px;
          font-size: 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
        }
        .cart-recommendations__item .select__select:focus {
          outline: none;
          border-color: #000;
        }
        .cart-recommendations__item .button.disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background-color: #ccc;
        }
        .cart-recommendations__item .button.disabled:hover {
          background-color: #ccc;
        }
        .cart-recommendations__item .select__select option:disabled {
          color: #999;
          font-style: italic;
          background-color: #f5f5f5;
        }
        .cart-recommendations__item .select__select option:disabled:hover {
          background-color: #f5f5f5;
        }
        .cart-recommendations__item .select__select:invalid {
          color: #999;
        }
        .cart-recommendations__item .select__select:only-child {
          cursor: default;
        }
        .cart-recommendations__item .select__select option:only-child {
          cursor: default;
        }
      `;
      document.head.appendChild(style);
    }
  }

  triggerCartUpdate() {
    // Update cart count in header
    this.updateCartCount();
    
    // Check if we're in cart drawer context or cart page context
    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('main-cart-items');
    
    if (cartDrawer) {
      // We're in cart drawer context - use custom event for cart drawer
      const reinitEvent = new CustomEvent('cart-drawer:reinitialize', {
        bubbles: true,
        detail: { source: 'cart-recommendations' }
      });
      cartDrawer.dispatchEvent(reinitEvent);
    } else if (cartPage) {
      // We're on cart page - use pub/sub system
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
      }
    } else {
      // Fallback - just update cart count
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-recommendations' });
      }
    }
  }

  updateCartCount() {
    // Update cart count in header if it exists
    const cartCountElements = document.querySelectorAll('.cart-count-bubble');
    if (cartCountElements.length > 0) {
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
            element.classList.toggle('hidden', cart.item_count === 0);
          });
        })
        .catch(error => console.error('Error updating cart count:', error));
    }
  }
}
  
customElements.define('cart-recommendations', CartRecommendations);
  